import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import {
  CARD_WRAPPER_TYPES,
  CONTENTFUL_PAGE_NAME,
  CONTENTFUL_SLUGS,
  DEFAULT_EMAIL_ADDRESS,
  NOTIFICATION_TYPES,
  UTM_PARAM_KEYS,
  UTM_PARAM_VALUES,
  WIZARD_CARDS_ELEVATION,
} from '../../../shared/constants';
import { ICard, ICardWrapper } from '../../../types/shared';

import { DEQ_COMPS } from '../../../utils/deq';
import { EMAIL_STATES } from '../../../utils/forms/constants';
import { ReactMarkdownExtended } from '../../../utils/generic';
import { renderBodyItem } from '../../../utils/generic/renderBodyItem';
import { createUtmParam } from '../../../utils/string-utils';

import mq from '../../../styles/breakpoints';
import Spacings from '../../../styles/Spacings';

import { handleReCaptcha } from '../../../services/re-captcha.service';
import { useWizardStore } from '../../../state/wizard/Wizard.store';
import FormStatusCard from '../../molecules/form-status-card/FormStatusCard';

import {
  parseBody,
  parseEmailBody,
  parseEmailTemplate,
} from './WizardResultLayout.helpers';
import {
  TabWrapperExtended,
  WizardResultBodyItemName,
  WizardResultLayoutProps,
} from './WizardResultLayout.types';
import WizardResultList from './WizardResultList';
import WizardResultOverview from './WizardResultOverview';
import WizardResultsHeader from './WizardResultsHeader';
import WizardTabWrapper from './WizardTabWrapper';

const emailConfig = {
  toAddress: DEFAULT_EMAIL_ADDRESS,
  subject: (wizardName: string) =>
    `${wizardName} Wizard: Een gebruiker heeft interesse in de volgende onderwerpen:`,
};

const fallbackParentPage = {
  slug: CONTENTFUL_SLUGS.home,
  name: CONTENTFUL_PAGE_NAME.home,
  parentPage: undefined,
};

const iframeUtmParam = createUtmParam(
  UTM_PARAM_KEYS.campaign,
  UTM_PARAM_VALUES.dkpnIframe
);

const WizardResultLayout = ({
  name,
  header,
  body,
  results,
  queryTags,
  noResultCards,
  showResultTitles,
  enableCustomResultMail,
  onResetButtonClick,
  wizardName,
  wizardSlug,
  wizardParentPage,
  hasIframe = false,
  className,
}: WizardResultLayoutProps) => {
  const { getItem } = useWizardStore();
  const { regularResults, textResults, resultTags } = results;

  const renderContainerFullWidth = !!queryTags || resultTags.length >= 1;
  const parsedBody = parseBody(body);
  const headerProps =
    !!queryTags || regularResults.length ? header : noResultCards[0];
  const [emailState, setEmailState] = useState(EMAIL_STATES.idle);

  const handleSendEmailSuccess = () => {
    setEmailState(EMAIL_STATES.success);
  };

  const handleSendEmailError = () => {
    setEmailState(EMAIL_STATES.error);
  };

  const sendEmailWithCustomResult = async () => {
    const emailBody = parseEmailBody(textResults, getItem);
    const emailTemplate = parseEmailTemplate({
      toAddress: emailConfig.toAddress,
      subject: emailConfig.subject(name),
      body: emailBody,
    });

    handleReCaptcha(
      emailTemplate,
      handleSendEmailSuccess,
      handleSendEmailError
    );
  };

  useEffect(() => {
    // When the Wizard does not contain a form and when the results have checkbox with text filled in, send email.
    if (enableCustomResultMail && textResults.length) {
      setEmailState(EMAIL_STATES.pending);
      sendEmailWithCustomResult();
    }
  }, []);

  const parentPageObject = {
    name: wizardName,
    slug: wizardSlug,
    parentPage: wizardParentPage || fallbackParentPage,
  };

  return (
    <Container fullWidth={renderContainerFullWidth} className={className}>
      {emailState === EMAIL_STATES.pending && (
        <FormStatusCard
          statusType={NOTIFICATION_TYPES.loading}
          show={emailState === EMAIL_STATES.pending}
        >
          <ReactMarkdownExtended componentType={DEQ_COMPS.wizardResult}>
            Bezig met laden...
          </ReactMarkdownExtended>
        </FormStatusCard>
      )}
      {(emailState === EMAIL_STATES.idle ||
        emailState === EMAIL_STATES.success ||
        emailState === EMAIL_STATES.error) && (
        <>
          <WizardResultsHeader
            {...headerProps}
            onResetButtonClick={onResetButtonClick}
            extraLinkParams={hasIframe ? iframeUtmParam : ''}
          />
          {(!!regularResults.length || queryTags) && (
            <BodyWrapper>
              {parsedBody.map((item, index) => {
                if (item.name === WizardResultBodyItemName.TAB_WRAPPER) {
                  return (
                    <WizardTabWrapper
                      key={index}
                      wrapper={item as TabWrapperExtended}
                      results={regularResults}
                      renderCardTitles={showResultTitles}
                    />
                  );
                }
                if (
                  (item as ICard | ICardWrapper).type ===
                  CARD_WRAPPER_TYPES.wizardResultsWrapper
                ) {
                  return resultTags.length || queryTags ? (
                    <WizardResultOverview
                      key={index}
                      tags={resultTags}
                      queryTags={queryTags}
                    />
                  ) : (
                    <WizardResultList
                      key={index}
                      results={regularResults}
                      renderCardTitles={showResultTitles}
                      cardElevation={
                        hasIframe
                          ? WIZARD_CARDS_ELEVATION.high
                          : WIZARD_CARDS_ELEVATION.low
                      }
                      hasIframe={hasIframe}
                    />
                  );
                }
                return (
                  <StyledBodyItem key={index}>
                    {renderBodyItem(
                      item as ICard | ICardWrapper,
                      index,
                      '',
                      [],
                      '',
                      parentPageObject,
                      'resultPage'
                    )}
                  </StyledBodyItem>
                );
              })}
            </BodyWrapper>
          )}
        </>
      )}
    </Container>
  );
};

interface ContainerProps {
  fullWidth: boolean;
}

const Container = styled.div<ContainerProps>`
  margin: 0 auto;
  max-width: ${({ fullWidth }) => (fullWidth ? '1082px' : '736px')};
  padding: ${Spacings.S};

  ${mq.from.breakpoint.M`
    padding: ${Spacings.S};
  `}

  ${mq.from.breakpoint.L`
    padding: ${Spacings.M};
  `}
`;

const BodyWrapper = styled.div`
  margin-bottom: ${Spacings.XL};
`;

const StyledBodyItem = styled.div`
  margin: 0 ${Spacings.S} 64px;

  ${mq.from.breakpoint.M`
    margin: 0 0 64px;
  `}

  ${mq.from.breakpoint.L`
    margin: 0 -120px 80px;
  `}

  ${mq.from.breakpoint.XL`
    margin: 0 -268px 80px;
  `}

   > * {
    margin-top: ${Spacings.XXXL};

    ${mq.from.breakpoint.L`
      margin-top: 80px;
    `}

    &:first-child {
      margin-top: ${Spacings.XXXL};

      ${mq.from.breakpoint.L`
        margin-top: 80px;
      `}
    }
  }
`;

export default WizardResultLayout;
